/******************************************************************************
 *** BgtSpcAtrzRtso ** 특별회계기금 결재 상신
*******************************************************************************/
import React from "react";
import Util from "@/js/common/Util";
import TextBox from "@/js/common/ui/input/TextBox";
import SelectBox from "@/js/common/ui/input/SelectBox";
import StyledTabs from "@/js/common/ui/StyledTabs";
import { Tab, TabList, TabPanel } from "react-tabs";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";
import BgtSpcAtrzDpstList from "./BgtSpcAtrzDpstList";
import BgtSpcAtrzExpndList from "./BgtSpcAtrzExpndList";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "../../common/DialogTitleBar";
import BgtSpcAtrzTitleDialog from "./dialog/BgtSpcAtrzTitleDialog";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";

interface Props {
    systemCode: string;
}

const BgtSpcAtrzRtso = ( {systemCode}: Props ) => {
    const userInfo = useRecoilValue(Atom.userInfo);
    // 검색 조건
    const [upOgnzCd  , setUpOgnzCd  ] = React.useState<string>(userInfo.upOgnzCd);      // 상위조직코드(본부)
    const [ognzCd    , setOgnzCd    ] = React.useState<string>(userInfo.ognzCd  );      // 조직코드(지부)
    const [acntgYear , setAcntgYear ] = React.useState<string>(Util.getNowAcntgYear()); // 회계연도
    const [searchKywd, setSearchKywd] = React.useState<string>("");                     // 검색어(적요)
    const [callTime  , setCallTime  ] = React.useState<Date>(new Date()); // 재검색 설정

    // Tabs
    const [tabIndex, setTabIndex] = React.useState<number>(0); // 0: 수입, 1: 지출
    const [procBgtSbjctSeCd, setProcBgtSbjctSeCd] = React.useState<string>(""); // 구분코드(01: 수입, 02: 지출)

    const [incomeCheckArr,  setIncomeCheckArr] = React.useState<any[]>([]); // 수입결의서 체크 리스트
    const [expenseCheckArr, setExpenseCheckArr] = React.useState<any[]>([]);  // 지출결의서 체크 리스트

    const [atrzTtlCn             , setAtrzTtlCn             ] = React.useState<string>(""); // 결재 제목
    const [spcAtrzTitleDialogOpen, setSpcAtrzTitleDialogOpen] = React.useState<boolean>(false); // 결재 제목 입력 모달 오픈

    const [atrzProcessing, setAtrzProcessing] = React.useState(false);  // 결재 상신 처리 중 확인 상태값

    // 특별회계기금 타이틀 설정
    const [systemName, setSystemName] = React.useState<string>("");

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "C1") {
            setSystemName("복지기금");
        } else if (systemCode === "C2") {
            setSystemName("자립기금");
        } else if (systemCode === "C3") {
            setSystemName("발전기금");
        } else if (systemCode === "C4") {
            setSystemName("희생자부조금");
        } else if (systemCode === "C5") {
            setSystemName("퇴직급여충당금");
        }
    }, [systemCode]);

    // 특별회계기금 결재상신 처리
    function doApproval() {
        if (atrzProcessing) return;

        AppContext.openAlert({
            title: "확인",
            msg: "결재상신을 진행하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "결재상신",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/atrz/process-bgt-spc-atrz-rtso.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        procBgtSbjctSeCd: procBgtSbjctSeCd, // 구분코드(수입[01], 지출[02])
                        dataList        : procBgtSbjctSeCd === "01" ? incomeCheckArr : expenseCheckArr,
                        spcAcntgSeCd    : systemCode,   // 시스테코드
                        atrzTtlCn       : atrzTtlCn     // 결재 제목
                    },
                    onSuccessFn: (res: any) => {
                        const procCnt = res.result.procCount; // 처리된 건수
                        const procSeNm = res.result.procSeCd === "01" ? "수입결의서" : "지출결의서"; // 적용된 구분코드명

                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "결재상신 완료",
                            msg: procCnt + "건의 " + procSeNm + " 결재상신이 완료되었습니다.",
                            icon: "check"
                        });
                        setSpcAtrzTitleDialogOpen(false);
                        setAtrzProcessing(false); // 결재버튼 활성화
                        setAtrzTtlCn(""); // 결재 제목
                        setIncomeCheckArr([]);
                        setExpenseCheckArr([]);
                        setCallTime(new Date());
                    },
                });
            },
            handleClose: () => {    // 취소
                setAtrzProcessing(false); // 결재버튼 활성화
            }
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">{systemName} 결재 상신</span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 영역 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC flxWrap gap14">
                    {/* 소속 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox value={upOgnzCd}
                            width={150} height={36} fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setUpOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 지부 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox value={ognzCd}
                            width={180} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#F6F6F6" paddingRight={8}
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            onChangeFunc={(data: string) => setOgnzCd(data)}
                            disabled={true}
                        />
                    </div>

                    {/* 회계연도 */}
                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox value={acntgYear}
                            width={120} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            background="#fff" paddingRight={8}
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            onChangeFunc={(data: string) => setAcntgYear(data)}
                        />
                    </div>

                    {/* 적요 */}
                    <div className="dpFlx aiC">
                        <span className="w50 fm-c fs15 fc333333 ls1 taL">적요</span>
                        <TextBox value={searchKywd}
                            width={200} height={36}
                            fontSize={14} fontFamily="fm-c" color="#333" letterSpacing={-1}
                            borderColor="#ddd" borderOnColor="#222" borderRadius={2}
                            style={{ backgroundColor: "#fff", padding: "0 12px" }}
                            placeholder="검색어 입력"
                            onChangeFunc={(text: string) => setSearchKywd(text)}
                        />
                    </div>

                    {/* 검색 버튼 */}
                    {/* <div className="dpFlx aiC mlA gap8">
                        <Button buttonName="검색"
                            width={70} height={40}
                            fontFamily="fm-b" fontSize={16} letterSpacing={-1} color="#fff"
                            backgroundColor="#007bff" borderRadius={5}
                            buttonClass="dpFlx aiC jcC"
                            onClickFunc={handleSearch}
                        />
                    </div> */}
                </div>
            </div>

            {/* 탭 */}
            <StyledTabs selectedIndex={tabIndex} className="mb10 tabFit">
                <TabList>
                    <Tab onClick={() => setTabIndex(0)}>수입 결의서</Tab>
                    <Tab onClick={() => setTabIndex(1)}>지출 결의서</Tab>
                </TabList>

                {/* ========================= 수입 탭 ========================= */}
                <TabPanel>
                    <BgtSpcAtrzDpstList
                        ognzCd     = {ognzCd}       // 조직
                        acntgYear  = {acntgYear}    // 회계연도
                        systemCode = {systemCode}   // 시스테코드
                        searchKywd = {searchKywd}   // 검색어(적요)
                        callTime   = {callTime}
                        incomeCheckArr    = {incomeCheckArr}    // 체크된 수입결의서 List
                        setIncomeCheckArr = {setIncomeCheckArr}
                        setProcBgtSbjctSeCd       = {setProcBgtSbjctSeCd}   // 구분코드(수입[01])
                        setSpcAtrzTitleDialogOpen = {setSpcAtrzTitleDialogOpen}
                        atrzProcessing            = {atrzProcessing}
                    ></BgtSpcAtrzDpstList>
                </TabPanel>

                {/* ========================= 지출 탭 ========================= */}
                <TabPanel>
                    <BgtSpcAtrzExpndList
                        ognzCd     = {ognzCd}       // 조직
                        acntgYear  = {acntgYear}    // 회계연도
                        systemCode = {systemCode}   // 시스테코드
                        searchKywd = {searchKywd}   // 검색어(적요)
                        callTime   = {callTime}
                        expenseCheckArr    = {expenseCheckArr}      // 체크된 지출결의서 List
                        setExpenseCheckArr = {setExpenseCheckArr}
                        setProcBgtSbjctSeCd       = {setProcBgtSbjctSeCd}   // 구분코드(지출[02])
                        setSpcAtrzTitleDialogOpen = {setSpcAtrzTitleDialogOpen}
                        atrzProcessing            = {atrzProcessing}
                    ></BgtSpcAtrzExpndList>
                </TabPanel>
            </StyledTabs>

            {/* 결재 제목 입력 모달 */}
            <Modal modalOpen={spcAtrzTitleDialogOpen} dim={true} visible={true}
                titleEl={<DialogTitleBar title="결재 제목" />}
                onCloseFunc={() => {
                    setAtrzTtlCn(""); // 결재 제목
                    setSpcAtrzTitleDialogOpen(false);
                }}
            >
                <BgtSpcAtrzTitleDialog
                    atrzTtlCn                 = {atrzTtlCn}    // 결재 제목
                    setAtrzTtlCn              = {setAtrzTtlCn}
                    setSpcAtrzTitleDialogOpen = {setSpcAtrzTitleDialogOpen}
                    doApproval                = {doApproval}   // 결재상신 처리
                    atrzProcessing            = {atrzProcessing}
                    setAtrzProcessing         = {setAtrzProcessing} // 결재상신 버튼 Disalbed
                />
            </Modal>
        </div>
    );
};

export default BgtSpcAtrzRtso;
